<template>
    <div
        v-for="(item, key) in cards"
        :key="`accordion-item-${key}`"
        class="item"
    >
        <button
            class="heading"
            @click="openItem(key)"
        >
            <h2 class="h5" :class="{'heading-open': (key === open)}">{{ item.title }}</h2>
            <div class="icon">
                <div class="fade-list">
                    <DSFontIcon
                        :key="item.id"
                        class="open-icons"
                        code="minus"
                        icon-style="line"
                        size="l"
                        :class="{ 'active': key === open }"
                    />
                    <DSFontIcon
                        :key="`${item.id}-control-closed`"
                        class="close-icons"
                        code="add"
                        size="l"
                        :class="{ 'active': key !== open }"
                    />
                </div>
            </div>
        </button>
        <DSHeightTransition :open="key === open">
            <div class="content">
                <div class="text">
                    <h3 v-if="item.byline" class="h4">{{ item.byline }}</h3>
                    <DSCFText :content="item.text" />
                    <div v-if="showLinksUnderContent(item)" class="links">
                        <template v-for="(link, lKey) in itemLinks(item)" :key="`card-${item.id}-${lKey}`">
                            <NuxtLink
                                class="ds-btn ds-btn--large"
                                v-bind="$cms.parseLink(link)"
                                :class="{ 'ds-btn--primary': lKey === 0, 'ds-btn--secondary': lKey === 1 }"
                            >
                                <span>{{ link.title }}</span>
                            </NuxtLink>
                        </template>
                    </div>
                </div>
                <DSFrontifyAsset
                    v-if="item.image"
                    :key="`image-${key}${opened.includes(key) ? '-opened' : ''}`"
                    fill="cover"
                    :asset="item.image"
                />
                <div v-if="hasProducts(item)" class="products">
                    <div v-for="product in item.products" :key="product.code" class="cl-product-card">
                        <DSAdeonaImage
                            v-if="product.image"
                            :image="product.image"
                            :alt="$t('products.graphic', { productName: product.attributes.product_name })"
                            class="cl-product-image"
                        />
                        <h2 class="cl-product-name">{{ product.attributes.product_name }}</h2>
                        <p class="cl-product-description">{{ product.attributes.short_description }}</p>
                    </div>
                </div>
                <div v-if="!showLinksUnderContent(item)" class="links">
                    <template v-for="(link, lKey) in itemLinks(item)" :key="`card-${item.id}-${lKey}`">
                        <NuxtLink
                            class="ds-btn"
                            v-bind="$cms.parseLink(link)"
                            :class="{ 'ds-btn--primary': lKey === 0, 'ds-btn--secondary': lKey === 1 }"
                        >
                            {{ link.title }}
                        </NuxtLink>
                    </template>
                </div>
            </div>
        </DSHeightTransition>
    </div>
</template>
<script setup>
import { ref } from 'vue';

const props = defineProps({
    cards: {
        type: [ Array ],
        required: false,
        default: () => [],
    },
    firstOpen: {
        type: Boolean,
        required: false,
        default: false,
    },
});

const open = ref(props.firstOpen ? 0 : -1);
const opened = ref([]);
const itemLinks = item => (item.links ?? []).filter(link => link.title && link.url);
const hasLinks = item => itemLinks(item).length;
const hasProducts = item => item?.products?.length;
const showLinksUnderContent = item => !hasProducts(item) && hasLinks(item);

const openItem = item => {
    if (item === open.value) {
        open.value = -1;
    } else {
        open.value = item;
    }
    if (!opened.value.includes(item)) {
        opened.value.push(item);
    }
};

</script>
<style scoped>
.item {
  will-change: height;
}
.item:focus-visible {
  outline: var(--color-base-grey-500) auto 1px;
}
.item .heading {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem 0;
  cursor: pointer;
  text-align: left;
}
.item .heading .h5 {
  text-transform: uppercase;
  margin-bottom: 0;
}
.item .icon {
  color: var(--color-base-grey-900);
}
.item .open-icons :deep(svg) {
  fill: var(--color-primary-900);
}
.item .heading-open {
  color: var(--color-primary-900);
}
.item .close-icons {
  color: var(--color-base-grey-500);
}
.item .heading:hover .h5 {
  color: var(--color-primary-900);
}
.item .heading:hover .icon :deep(svg) {
  fill: var(--color-primary-900);
}
.item:not(:last-child) {
  border-bottom: 2px solid var(--color-base-grey-300);
}
.item .content {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 0 4rem;
  padding: 1rem 0 2rem;
}
.item .content .byline {
  text-transform: uppercase;
  letter-spacing: 0.02em;
  margin-bottom: 0;
  font-size: 0.75rem;
  font-weight: 700;
}
.item .content .byline + h3 {
  margin-top: 0;
}
.item .content .links {
  margin-top: 2rem;
}
.item .content .links > *:not(:last-child) {
  margin-right: 0.5rem;
}
.item .content .links > * {
  margin-top: 1rem;
}
.item .content .image,
.item .content .video {
  align-self: center;
  padding-top: 75%;
}
@media (max-width: 767px) {
  .item .content {
    grid-template-columns: 1fr;
    padding-left: 0;
    padding-right: 0;
    padding-top: 1.5rem;
  }
  .item .content .image,
  .item .content .video {
    order: -1;
    margin-bottom: 1.5rem;
  }
}

.products {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  grid-column-start: 1;
  grid-column-end: 3;
  margin-top: 3.5rem;
}

.cl-product-card {
  background-color: var(--cl-product-card, #eef1f6);
  padding: 1.5rem;
  border-radius: 1.75rem;
}

.cl-product-image {
  border-radius: 0.75rem;
  margin-bottom: 2rem;
}

.cl-product-name {
  font-size: 1.25rem;
  margin-bottom: 1.5rem;
  font-weight: 500;
}

.fade-list {
  display: inline-flex;
  justify-content: center;
  position: relative;
  width: 2rem;
  height: 2rem;
}
.fade-list .icon {
  transition: opacity 0.5s ease-out;
  position: absolute;
  width: 1.5rem;
  height: 1.5rem;
  opacity: 0;
}
.fade-list .icon.active {
  opacity: 1;
}

@media (min-width: 992px) {
  .cl-product-name {
    font-size: 1.625rem;
  }
}
</style>
